import { useState } from "react";
import { Star, StarBorder } from "@mui/icons-material";
import { submitToGoogleForm } from "../API";
import { toast } from "react-toastify";

function CustomerFeedback({ orderDetails, orderID, onFeedbackSubmitted }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mask phone number - show only last 4 digits
  const maskPhoneNumber = (phone) => {
    if (!phone) return "N/A";
    const phoneStr = phone.toString();
    if (phoneStr.length <= 4) return phoneStr;
    const lastFour = phoneStr.slice(-4);
    return `***-***-${lastFour}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    setIsSubmitting(true);
    try {
      const feedbackData = {
        rating,
        comment: comment.trim(),
        customerName: orderDetails?.customerName || "",
        customerPhone: orderDetails?.phone || "",
        deliveryLocation: orderDetails?.orderDropLocation?.locationName || "",
      };
      await submitToGoogleForm(orderID, feedbackData);
      toast.success("Thank you for your feedback!");
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted(feedbackData);
      }
      // Reset form
      setRating(0);
      setComment("");
    } catch (error) {
      toast.error("Failed to submit feedback. Please try again.");
      console.error("Feedback submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-6 p-4 bg-white border border-[#E6E6E6] rounded-lg" style={{ maxWidth: "840px", width: "100%" }}>
      <h3 className="text-lg font-bold mb-4">Customer Feedback</h3>
      
      {/* Customer Information */}
      <div className="mb-4 pb-4 border-b border-[#E6E6E6]">
        <div className="flex flex-col gap-2">
          <div>
            <span className="text-sm text-gray-600">Customer Name: </span>
            <span className="text-sm font-semibold">
              {orderDetails?.customerName || "N/A"}
            </span>
          </div>
          <div>
            <span className="text-sm text-gray-600">Phone Number: </span>
            <span className="text-sm font-semibold">
              {maskPhoneNumber(orderDetails?.phone)}
            </span>
          </div>
        </div>
      </div>

      {/* Rating Section */}
      <div className="mb-4">
        <label className="block text-sm font-semibold mb-2 text-center">
          Rate your experience <span className="text-accent">*</span>
        </label>
        <div className="flex gap-1 justify-center items-center">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              className="focus:outline-none flex items-center justify-center"
              disabled={isSubmitting}
            >
              {(hoverRating || rating) >= star ? (
                <Star
                  style={{
                    color: "#FFD700",
                    fontSize: "32px",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                  }}
                />
              ) : (
                <StarBorder
                  style={{
                    color: "#D3D3D3",
                    fontSize: "32px",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                  }}
                />
              )}
            </button>
          ))}
        </div>
        {rating > 0 && (
          <p className="text-xs text-gray-600 mt-1 text-center">
            {rating} out of 5 stars
          </p>
        )}
      </div>

      {/* Comment Section */}
      <div className="mb-4">
        <label
          htmlFor="feedback-comment"
          className="block text-sm font-semibold mb-2"
        >
          Comments (Optional)
        </label>
        <textarea
          id="feedback-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Share your experience with us..."
          className="w-full p-3 border border-[#E6E6E6] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A16F] resize-none"
          rows="4"
          disabled={isSubmitting}
          maxLength={500}
        />
        <p className="text-xs text-gray-500 mt-1">
          {comment.length}/500 characters
        </p>
      </div>

      {/* Submit Button */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={isSubmitting || rating === 0}
        className={`w-full py-3 px-4 rounded-lg font-bold text-white transition-colors ${
          isSubmitting || rating === 0
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-[#00A16F] hover:bg-[#008a5e]"
        }`}
      >
        {isSubmitting ? "Submitting..." : "Submit Feedback"}
      </button>
    </div>
  );
}

export default CustomerFeedback;

